// This file is meant for asset sorting

const assert = require('assert');
const { Observable, BehaviorSubject } = require('rxjs');
const lget = require('lodash/get');
const lset = require('lodash/set');
const orderBy = require('lodash/orderBy');
const { isRef, toObjectHash, isPending, watchAll, isObservable } = require('../../muster-utils');

function createSource({ get }, maybeThunk) {
  if (typeof maybeThunk === 'string') {
    return new BehaviorSubject(maybeThunk);
  }

  if (isRef(maybeThunk)) {
    return get(maybeThunk.value)
      .filter((pathValues) => pathValues.every(({ value }) => !isPending(value)))
      .map(([{ value }]) => value);
  }

  assert(typeof maybeThunk === 'function', 'Expected value to be a thunk');
  const obs = maybeThunk({ get });
  assert(isObservable(obs), 'Thunk did not return an observable');
  return obs;
}

/* eslint-disable complexity */
function validateOptions(options) {
  const {
    sortByKey,
    fromList,
    toList,
    listLength,
    isAscending,
  } = options;

  assert(
    !sortByKey || (
      typeof sortByKey === 'string' || isRef(sortByKey) || typeof sortByKey === 'function'
    ),
    'Invalid `sortByKey` value. Expects a string, ref or a function.'
  );
  assert(typeof fromList === 'string', 'Invalid `fromList` value. Expects a string.');
  assert(typeof toList === 'string', 'Invalid `toList` value. Expects a string.');
  assert(
    isRef(listLength) || typeof listLength === 'function',
    'Invalid `listLength` value. Expects a ref or a function.'
  );
  assert(
    !isAscending || (isRef(isAscending) || typeof isAscending === 'function'),
    'Invalid `isAscending` value. Expects a ref or a function.'
  );
}
/* eslint-enable complexity */

/**
 * @typedef SortOptions
 * @type {object}
 * @property {string} fromList - underlying list path to sort on
 * @property {string} toList - list path to map values back to
 * @property {(Ref|function)} listLength - reference or thunk to get the list length
 * @property {?(Ref|function)} isAscending - reference or thunk to determine list order
 * @property {?(Ref|function|string)} sortByKey - string, reference or thunk to
 *                                                determine what property to sort on
 */
/**
 * Create a handler that sorts a list
 * @param {SortOptions} options Sort configuration
 * @returns {RouteHandler} Route handler function
 *
 * @example
 * .route('positions.assetsSorted[{integers:index}].*', sortable({
 *   fromList: 'postions.assets',
 *   toList: 'postions.assetsSorted',
 *   listLength: ref('portfolio.assets.length'),
 *   isAscending: ref('ui.state.portfolio.ascending'),
 *   sortByKey: ({ get }) => (
 *    watchValues(get('ui.state.portfolio.selectedTitle'))
 *      .map(([selectedTitle]) => !selectedTitle ? null : `${selectedTitle}.value`)
 *   ),
 * }))
 */
function sortable(options) {
  const {
    sortByKey,
    fromList,
    toList,
    listOffset,
    listLength,
    isAscending,
  } = options;

  validateOptions(options);

  function deconstructDependentResults(_results) {
    const results = _results.slice();
    const length = results.shift();

    let sortBy = false;
    if (sortByKey) {
      sortBy = results.shift();
    }

    let ascending = true;
    if (isAscending) {
      ascending = results.shift();
    }

    let offset = 0;
    if (listOffset) {
      offset = results.shift();
    }

    return {
      length,
      sortBy,
      ascending,
      offset,
    };
  }

  function listSort(ascending, sortBy,offset, pathValues) {
    const obj = toObjectHash(pathValues);
    const list = lget(obj, fromList);
    let list1, finalsortedValues ,nulls , nonNulls;
    let sortedValues = list.slice();
    if(offset === 0){
      if (sortBy !== false) {
        if(sortBy){
          nulls = sortedValues.filter(x => x[sortBy] === null);      // WMCE-24126 -For handing null values in asset sorting seperate the  null values ,apply order by on non null dataset and then concatenate the null array at the end.
          nonNulls = sortedValues.filter(x => x[sortBy] !== null);
          list1 = nonNulls.slice();
          sortedValues = orderBy(
            list1,
            [(item) => {
              if (item) {
                if (sortBy === null) {
                  return Number.MIN_SAFE_INTEGER;
                } else {
                  return lget(item, sortBy);
                }
              } else {
                if (ascending === null) {
                  return Number.MAX_SAFE_INTEGER;
                } else {
                  return (ascending ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER);
                }
              }
            },'secID'],                                //WMCE-24933 -secondary sorting condition on position id in ascending order
             [ ascending ? 'asc' : 'desc','asc']
          );
        }
        else{                         //Wmce-24933- For default loading of dataset no secondary condition is  applied
            list1=list.slice();
            sortedValues = orderBy(
              list1,
              (item) => {
                if (item) {
                  if (sortBy === null) {
                    return Number.MIN_SAFE_INTEGER;
                  } else {
                    return lget(item, sortBy);
                  }
                } else {
                  if (ascending === null) {
                    return Number.MAX_SAFE_INTEGER;
                  } else {
                    return (ascending ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER);
                  }
                }
              },                                //WMCE-24933 -secondary sorting condition on position id in ascending order
               ascending ? 'asc' : 'desc'
            );
        }
        // sortedValues = orderBy(
        //   list1,
        //   [(item) => {
        //     if (item) {
        //       if (sortBy === null) {
        //         return Number.MIN_SAFE_INTEGER;
        //       } else {
        //         return lget(item, sortBy);
        //       }
        //     } else {
        //       if (ascending === null) {
        //         return Number.MAX_SAFE_INTEGER;
        //       } else {
        //         return (ascending ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER);
        //       }
        //     }
        //   },'secID'],                                //WMCE-24933 -secondary sorting condition on position id in ascending order
        //    [ ascending ? 'asc' : 'desc','asc']
        // );
      }
    }
    finalsortedValues =sortedValues.slice();
    if(sortBy && offset===0){
      finalsortedValues.push(...nulls);
    }
    const resultObj = lset({}, toList, finalsortedValues);

    return pathValues.map(({ path: fromPath }) => {
      const toPath = fromPath.replace(fromList, toList);
      const value = lget(resultObj, toPath);
      return { path: toPath, value };
    });
  }

  return (requests, { get }) => {
    const sourceCreator = createSource.bind(null, { get });

    const dependentObs = [sourceCreator(listLength)];
    if (sortByKey) {
      dependentObs.push(sourceCreator(sortByKey));
    }
    if (isAscending) {
      dependentObs.push(sourceCreator(isAscending));
    }
    if (listOffset != null) {
      dependentObs.push(sourceCreator(listOffset));
    }

    // Start watching to dependent references and all sortBy references
    return Observable.combineLatest(dependentObs)
      .switchMap((results) => {
        const {
          length,
          ascending,
          sortBy,
          offset,
        } = deconstructDependentResults(results);

        const leafPaths = requests.reduce((memo, { req }) => (
          memo.add(req.params['*'])
        ), new Set());
        if (sortBy) {
          leafPaths.add(sortBy);
        }

        const baseListPaths = [...leafPaths].map(
          (leaf) => `${fromList}[${offset}...${length + offset}].${leaf}`
        );

        return watchAll(get(baseListPaths))
          .map(listSort.bind(null, ascending, sortBy , offset));
      });
  };
}

module.exports = sortable;